import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMediaQuery, useTheme, Collapse, IconButton, Checkbox } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Action_icon from "../../images/table/Actions_icon.svg";
import Person from "@mui/icons-material/Person";
import "./DynamicTable.css";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import sortingArrow1 from "../../images/sortingArrow1.svg";
import sortingArrow2 from "../../images/sortingArrow2.svg";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "20px",
  overflow: "auto",
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#647490",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#B7BFCC",
    borderRadius: "10px",
    margin: "5px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#647490 #B7BFCC",
}));

const ResponsiveTableCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 12px",
  minWidth: "120px",
  maxWidth: "300px",
  color: "#1F242E",
  fontSize: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "6px 8px",
    fontSize: "0.75rem",
    minWidth: "80px",
    maxWidth: "150px",
  },
}));

const SortableHeaderCell = styled(ResponsiveTableCell)(({ theme }) => ({
  cursor: "pointer",
  userSelect: "none",
  position: "relative",
  "&:hover": {
    backgroundColor: "#586280",
  },
  transition: "background-color 0.2s ease",
}));

const SortIconsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "8px",
  gap: "0px",
}));

const SortIcon = styled("div")(({ active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "16px",
  height: "16px",
  filter: active
    ? "invert(33%) sepia(99%) saturate(1352%) hue-rotate(180deg) brightness(95%) contrast(101%)"
    : "none",
  opacity: active ? 1 : 0.5,
  transition: "filter 0.2s ease, opacity 0.2s ease",
  "& img": {
    width: "100%",
    height: "100%",
  },
}));

const HeaderContent = styled("div")(({ isActions }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isActions ? "flex-end" : "flex-start",
  width: "100%",
  fontWeight: 600,
}));

const ActionContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-end",
}));

const ActionMenu = styled("div")(() => ({
  position: "fixed",
  zIndex: 1300,
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
  borderRadius: "8px",
  width: "160px",
  overflow: "hidden",
  transition: "opacity 0.2s ease-in-out",
}));

const ProfileImage = styled("img")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  marginRight: "8px",
  border: "1px solid #183059",
  verticalAlign: "middle",
  objectFit: "cover",
  [theme.breakpoints.down("sm")]: {
    width: "20px",
    height: "20px",
    marginRight: "6px",
  },
}));

const ProfileIcon = styled(Person)(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  marginRight: "8px",
  border: "1px solid #183059",
  verticalAlign: "middle",
  color: "#647490",
  backgroundColor: "#f5f5f5",
  padding: "2px",
  [theme.breakpoints.down("sm")]: {
    width: "20px",
    height: "20px",
    marginRight: "6px",
  },
}));

// Sort states: 'none', 'asc', 'desc'
const SORT_STATES = {
  NONE: "none",
  ASC: "asc",
  DESC: "desc",
};

export default function DynamicTable({
  headers = [],
  data = [],
  onActionClick = () => {},
  onSortedDataChange = null,
  actions = [],
  loading = false,
  skeletonRowCount = 5,
  translationNamespace = "common",
  columnWidths = {},
  expandedRowId = null,
  onExpandClick = null,
  renderExpandedRow = null,
  hideActionsMenu = false,
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange = null,
  rowKey = "id",
}) {
  console.log(data);
  const [activeRow, setActiveRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    placement: "bottom",
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: SORT_STATES.NONE,
  });
  // eslint-disable-next-line no-unused-vars
  const [originalData, setOriginalData] = useState([]);

  const actionMenuRef = useRef(null);
  const actionIconRefs = useRef([]);
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("");

  // Store original data order when data changes
  useEffect(() => {
    setOriginalData([...data]);
    // Reset sort when data changes significantly (like after filtering)
    if (data.length === 0) {
      setSortConfig({ key: null, direction: SORT_STATES.NONE });
    }
  }, [data]);

  // Handle sorting
  const handleSort = (headerKey) => {
    // Don't sort actions column
    if (isActionsColumn({ key: headerKey })) {
      return;
    }

    setSortConfig((prevConfig) => {
      if (prevConfig.key !== headerKey) {
        // New column - start with ascending
        return { key: headerKey, direction: SORT_STATES.ASC };
      } else {
        // Same column - cycle through states
        switch (prevConfig.direction) {
          case SORT_STATES.ASC:
            return { key: headerKey, direction: SORT_STATES.DESC };
          case SORT_STATES.DESC:
            return { key: headerKey, direction: SORT_STATES.NONE };
          default:
            return { key: headerKey, direction: SORT_STATES.ASC };
        }
      }
    });
  };

  // Sort the data based on current sort config
  // Sort the data based on current sort config
  const sortedData = useMemo(() => {
    // If no data, return empty array
    if (data.length === 0) {
      return [];
    }

    if (sortConfig.direction === SORT_STATES.NONE || !sortConfig.key) {
      return data; // Use data prop directly instead of originalData
    }

    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle React elements (like status icons) - convert to string for comparison
      if (React.isValidElement(aValue)) {
        aValue = aValue.props?.alt || aValue.props?.title || "";
      }
      if (React.isValidElement(bValue)) {
        bValue = bValue.props?.alt || bValue.props?.title || "";
      }

      // Handle null/undefined values
      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      // Numeric comparison if both are numbers
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === SORT_STATES.ASC
          ? aNum - bNum
          : bNum - aNum;
      }

      // String comparison
      if (sortConfig.direction === SORT_STATES.ASC) {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return sorted;
  }, [data, sortConfig]); // Changed dependency from originalData to data

  const prevSortedDataRef = useRef();
  useEffect(() => {
    if (onSortedDataChange) {
      const prev = prevSortedDataRef.current;
      if (
        prev &&
        prev.length === sortedData.length &&
        prev.every((item, index) => item === sortedData[index] || (item?.id !== undefined && item.id === sortedData[index]?.id))
      ) {
        return;
      }
      prevSortedDataRef.current = sortedData;
      onSortedDataChange(sortedData);
    }
  }, [sortedData, onSortedDataChange]);

  console.log("Sorted Data:", sortedData);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target) &&
        !actionIconRefs.current.some((ref) => ref?.contains(event.target))
      ) {
        setActiveRow(null);
      }
    };

    const handleScroll = () => {
      setActiveRow(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (activeRow === null || !actionIconRefs.current[activeRow]) return;

    const iconRect = actionIconRefs.current[activeRow].getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight = 44 * actions.length + 10;
    const menuWidth = 160;

    const spaceBelow = viewportHeight - iconRect.bottom;
    const spaceAbove = iconRect.top;
    const placement =
      spaceBelow >= menuHeight || spaceBelow > spaceAbove ? "bottom" : "top";

    let left = Math.min(
      iconRect.left - menuWidth + iconRect.width,
      viewportWidth - menuWidth - 10
    );
    left = Math.max(10, left);

    let top =
      placement === "bottom"
        ? Math.min(iconRect.bottom + 1, viewportHeight - menuHeight - 10)
        : Math.max(iconRect.top - menuHeight - 1, 10);

    setMenuPosition({ top, left, placement });
  }, [activeRow, actions.length, isSmallScreen]);

  useEffect(() => {
    actionIconRefs.current = Array(data.length)
      .fill()
      .map((_, i) => actionIconRefs.current[i] || null);
  }, [data.length]);

  const handleActionClick = (event, rowIndex) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const menuHeight = 44 * actions.length + 10;
    const menuWidth = 160;
    const top =
      rect.bottom + 1 > viewportHeight - menuHeight
        ? rect.top - menuHeight - 1
        : rect.bottom + 1;
    const left = Math.max(
      10,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 10)
    );

    setMenuPosition({
      top,
      left,
      placement: top > rect.bottom ? "top" : "bottom",
    });
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const handleActionItemClick = (actionId, rowIndex) => {
    const actionObj = actions.find((action) => action.id === actionId);
    onActionClick(actionId, rowIndex, actionObj);
    setActiveRow(null);
  };

  const handleKeyDown = (event, actionId, rowIndex) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleActionItemClick(actionId, rowIndex);
    }
  };

  const isActionsColumn = (header) => header.key === ACTIONS_COLUMN_ID;

  const renderSortIcons = (headerKey) => {
    if (isActionsColumn({ key: headerKey })) {
      return null;
    }

    const isCurrentSort = sortConfig.key === headerKey;
    const sortDirection = isCurrentSort
      ? sortConfig.direction
      : SORT_STATES.NONE;

    return (
      <SortIconsContainer>
        <SortIcon active={sortDirection === SORT_STATES.ASC}>
          <img
            src={sortingArrow2}
            alt="Sort ascending"
            style={{ width: "8px", height: "8px" }}
          />
        </SortIcon>
        <SortIcon active={sortDirection === SORT_STATES.DESC}>
          <img
            src={sortingArrow1}
            alt="Sort descending"
            style={{ width: "8px", height: "8px" }}
          />
        </SortIcon>
      </SortIconsContainer>
    );
  };

  const getRowKeyValue = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    return row[rowKey] ?? row.originalData?.[rowKey] ?? row.id ?? row.originalData?.id ?? index;
  };

  const renderSkeletonRows = () => {
    return Array(skeletonRowCount)
      .fill(0)
      .map((_, rowIndex) => (
        <TableRow
          key={`skeleton-${rowIndex}`}
          sx={{
            height: "50px",
            "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
            borderBottom: "1px solid #ddd",
          }}
        >
          {selectable && (
            <ResponsiveTableCell
              sx={{
                width: "1%",
                whiteSpace: "nowrap",
                padding: "4px 8px",
                textAlign: "center",
              }}
            >
              <Skeleton variant="rectangular" width={18} height={18} sx={{ margin: "auto" }} />
            </ResponsiveTableCell>
          )}
          {headers.map((header, colIndex) => (
            <ResponsiveTableCell
              key={`skeleton-${rowIndex}-${colIndex}`}
              align={isActionsColumn(header) ? "right" : "left"}
              sx={{ width: columnWidths[header.key] || "auto" }}
            >
              {isActionsColumn(header) ? (
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                  sx={{ ml: "auto" }}
                />
              ) : (
                <Skeleton
                  variant="text"
                  width={`${60 + Math.random() * 40}%`}
                />
              )}
            </ResponsiveTableCell>
          ))}
        </TableRow>
      ));
  };

  return (
    <ResponsiveTableContainer ref={tableContainerRef}>
      <Table
        sx={{
          tableLayout: "auto",
          width: "100%",
          minWidth: isSmallScreen ? "100%" : "800px",
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#647490" }}>
            {selectable && (
              <ResponsiveTableCell
                sx={{
                  backgroundColor: "#647490",
                  width: "1%",
                  whiteSpace: "nowrap",
                  padding: "4px 8px",
                  textAlign: "center",
                  borderTopLeftRadius: "10px",
                  borderBottom: "none",
                }}
              >
                <Checkbox
                  size="small"
                  indeterminate={
                    selectedRowKeys.length > 0 &&
                    selectedRowKeys.length < sortedData.length
                  }
                  checked={
                    sortedData.length > 0 &&
                    sortedData.every((r, i) => selectedRowKeys.includes(getRowKeyValue(r, i)))
                  }
                  onChange={(e) => {
                    if (onSelectionChange) {
                      if (e.target.checked) {
                        onSelectionChange(sortedData.map((r, i) => getRowKeyValue(r, i)));
                      } else {
                        onSelectionChange([]);
                      }
                    }
                  }}
                  sx={{
                    color: "#FFFFFF",
                    "&.Mui-checked": { color: "#FFFFFF" },
                    "&.MuiCheckbox-indeterminate": { color: "#FFFFFF" },
                    padding: "2px",
                  }}
                />
              </ResponsiveTableCell>
            )}
            {headers.map((header, index) => {
              const HeaderCellComponent = isActionsColumn(header)
                ? ResponsiveTableCell
                : SortableHeaderCell;

              return (
                <HeaderCellComponent
                  key={header.key || index}
                  sx={{
                    backgroundColor: "#647490",
                    color: "white",
                    fontWeight: "bold",
                    textAlign: isActionsColumn(header) ? "right" : "left",
                    borderTopLeftRadius: index === 0 && !selectable ? "10px" : 0,
                    borderTopRightRadius:
                      index === headers.length - 1 ? "10px" : 0,
                    borderBottom: "none",
                    whiteSpace: "normal",
                    width: columnWidths[header.key] || "auto",
                    fontSize: "20px",
                  }}
                  onClick={() => handleSort(header.key)}
                >
                  {loading ? (
                    <Skeleton width="80%" />
                  ) : (
                    <HeaderContent isActions={isActionsColumn(header)}>
                      {isActionsColumn(header) ? t("Actions") : t(header.value)}
                      {renderSortIcons(header.key)}
                    </HeaderContent>
                  )}
                </HeaderCellComponent>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            renderSkeletonRows()
          ) : sortedData.length > 0 ? (
            sortedData.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
              <TableRow
                id={row.id != null ? `driver-row-${row.id}` : undefined}
                sx={{
                  fontSize: "16px",
                  height: "50px",
                  "&:nth-of-type(odd)": {
                    backgroundColor:
                      activeRow === rowIndex ? "#e3f2fd" : "#f5f5f5",
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor:
                      activeRow === rowIndex ? "#e3f2fd" : "#ffffff",
                  },
                  borderBottom: "1px solid #ddd",
                  ...(activeRow === rowIndex && {
                    backgroundColor: "#e3f2fd !important",
                  }),
                }}
              >
                {selectable && (
                  <ResponsiveTableCell
                    sx={{
                      width: "1%",
                      whiteSpace: "nowrap",
                      padding: "4px 8px",
                      textAlign: "center",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedRowKeys.includes(getRowKeyValue(row, rowIndex))}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (onSelectionChange) {
                          const key = getRowKeyValue(row, rowIndex);
                          onSelectionChange(
                            selectedRowKeys.includes(key)
                              ? selectedRowKeys.filter((k) => k !== key)
                              : [...selectedRowKeys, key]
                          );
                        }
                      }}
                      sx={{
                        padding: "2px",
                        color: "#647490",
                        "&.Mui-checked": { color: "#185FA5" },
                      }}
                    />
                  </ResponsiveTableCell>
                )}
                {headers.map((header, colIndex) => (
                  <ResponsiveTableCell
                    key={`${rowIndex}-${header.key || colIndex}`}
                    align={isActionsColumn(header) ? "right" : "left"}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: columnWidths[header.key] || "auto",
                    }}
                  >
                    {isActionsColumn(header) ? (
                      <ActionContainer>
                        {onExpandClick && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onExpandClick(row); }}
                            sx={{ mr: 0.5, color: "#647490" }}
                            aria-label="expand row"
                          >
                            {expandedRowId === row.id
                              ? <KeyboardArrowUpIcon />
                              : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                        {!hideActionsMenu && (
                          <div
                            className="action-icon"
                            ref={(el) => (actionIconRefs.current[rowIndex] = el)}
                            onClick={(e) => handleActionClick(e, rowIndex)}
                            style={{ cursor: "pointer" }}
                            role="button"
                            aria-label={t("actions")}
                          >
                            <img
                              style={{ width: "20px", height: "20px" }}
                              src={Action_icon}
                              alt={t("actions")}
                            />
                          </div>
                        )}
                      </ActionContainer>
                    ) : header.key === "Driver ID" ? (
                      <Tooltip
                        title={String(row[header.key] || "")}
                        disableHoverListener={
                          String(row[header.key] || "").length < 20
                        }
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {row.originalData?.image_url ? (
                            <ProfileImage
                              src={row.originalData.image_url}
                              alt={`${row["Driver Name"]} profile`}
                            />
                          ) : (
                            <ProfileIcon />
                          )}
                          {row[header.key] || "-"}
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={String(row[header.key] || "")}
                        disableHoverListener={
                          String(row[header.key] || "").length < 20
                        }
                      >
                        <span
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {row[header.key] || "-"}
                        </span>
                      </Tooltip>
                    )}
                  </ResponsiveTableCell>
                ))}
              </TableRow>
              {expandedRowId === row.id && renderExpandedRow && (
                <TableRow key={`expanded-${rowIndex}`} sx={{ backgroundColor: "#E8EAEE" }}>
                  <TableCell colSpan={headers.length + (selectable ? 1 : 0)} sx={{ p: 0, border: 0, backgroundColor: "#E8EAEE" }}>
                    <Collapse in={expandedRowId === row.id} timeout="auto" unmountOnExit>
                      <div style={{ padding: "0 16px 16px 16px", backgroundColor: "#E8EAEE" }}>
                        {renderExpandedRow(row)}
                      </div>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
            ))
          ) : (
            <TableRow>
              <ResponsiveTableCell
                colSpan={headers.length + (selectable ? 1 : 0)}
                sx={{ textAlign: "center", height: "100px" }}
              >
                {t("noDataAvailable")}
              </ResponsiveTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {activeRow !== null && (
        <ActionMenu
          ref={actionMenuRef}
          className="action-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          {actions.map((action) => (
            <div
              key={action.id}
              className={`action-menu-item ${action.className || ""}`}
              onClick={() => handleActionItemClick(action.id, activeRow)}
              onKeyDown={(e) => handleKeyDown(e, action.id, activeRow)}
              style={{
                padding: "10px 15px",
                cursor: "pointer",
                display: "flex",
                color: "#1F242E",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
              role="menuitem"
              tabIndex={0}
            >
              {action.icon && (
                <img
                  src={action.icon}
                  alt={t(action.label)}
                  style={{ width: "16px", height: "16px" }}
                />
              )}
              {t(action.label)}
            </div>
          ))}
        </ActionMenu>
      )}
    </ResponsiveTableContainer>
  );
}

DynamicTable.propTypes = {
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  onActionClick: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      className: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  skeletonRowCount: PropTypes.number,
  translationNamespace: PropTypes.string,
  columnWidths: PropTypes.object,
  fallbackImage: PropTypes.string,
};
